import React, { useState, useEffect } from 'react';
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
  Font,
  Svg,
  Circle,
  Path,
  Link,
} from '@react-pdf/renderer';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { formatPhoneNumber } from '../../utils/phone';
import {
  TrainingModuleDto,
  TrainingTopicDto,
  TrainingContentDto,
} from '../../api/types/dtos/training';

// Register a standard font that comes with react-pdf and disable hyphenation
Font.register({
  family: 'Helvetica',
  fonts: [
    {
      src: 'Helvetica',
    },
    {
      src: 'Helvetica-Bold',
      fontWeight: 'bold',
    },
  ],
});

// Disable automatic hyphenation to avoid inserting hyphens inside long URLs
// This callback forces react-pdf to return the original word without hyphenation.
Font.registerHyphenationCallback((word) => [word]);

// Design System Colors (matching _variables.scss for PDF)
const colors = {
  // Primary
  primary: '#0b0f28',
  primaryLight: '#0b0f28',
  
  // Secondary
  secondary: '#e7ecff',
  
  // Tertiary
  tertiary: '#eff2ff',
  tertiaryLight: '#f7f9ff',
  
  // Text
  textPrimary: '#0b0f28',
  textSecondary: '#757575',
  textDisabled: '#9e9e9e',
  
  // Background
  backgroundPaper: '#ffffff',
  
  // Border
  borderColor: '#afbeff',
  divider: '#e0e0e0',
  
  // Status
  success: '#4caf50',
  warning: '#ff9800',
  info: '#03a9f4',
  infoDark: '#01579b',
  error: '#d32f2f',
  
  // Training
  trainingCheckbox: '#FFBD59',
  trainingCheckboxBg: '#E7ECFF',
  trainingProgressBg: '#AFBEFF',
};

// Create styles
const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: colors.tertiary,
    paddingTop: 24,
    paddingBottom: 50,
    paddingHorizontal: 24,
    fontFamily: 'Helvetica',
  },
  topHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.backgroundPaper,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: colors.borderColor,
    padding: 14,
    marginBottom: 16,
  },
  topHeaderLogo: {
    width: 90,
    height: 60,
    objectFit: 'contain',
  },
  verticalDivider: {
    width: 1,
    height: '80%',
    backgroundColor: colors.divider,
    marginHorizontal: 20,
  },
  topHeaderTextContainer: {
    flexDirection: 'column',
  },
  topHeaderTitle: {
    fontSize: 20,
    color: colors.textPrimary,
    fontFamily: 'Helvetica',
    letterSpacing: 1,
  },
  topHeaderSubtitle: {
    fontSize: 26,
    color: colors.primary,
    fontFamily: 'Helvetica-Bold',
    fontWeight: 'bold',
  },
  contentContainer: {
    flexDirection: 'row',
    flexWrap: 'nowrap',
    gap: 16,
    width: '100%',
  },
  leftColumn: {
    width: '55%',
  },
  rightColumn: {
    width: '42%',
  },
  fullWidthSection: {
    width: '100%',
    marginTop: 16,
  },
  section: {
    marginBottom: 16,
    padding: 16,
    backgroundColor: colors.backgroundPaper,
    borderWidth: 1,
    borderColor: colors.borderColor,
    borderRadius: 16,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: colors.borderColor,
    paddingBottom: 16,
    backgroundColor: colors.backgroundPaper,
  },
  title: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 4,
    color: colors.textPrimary,
  },
  subtitle: {
    fontSize: 10,
    color: colors.textSecondary,
    marginBottom: 8,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 16,
    color: colors.textPrimary,
  },
  row: {
    flexDirection: 'row',
    marginBottom: 8,
    alignItems: 'center',
  },
  label: {
    width: '40%',
    fontSize: 10,
    color: colors.textSecondary,
  },
  value: {
    flex: 1,
    fontSize: 10,
    color: colors.textPrimary,
    fontWeight: 'bold',
  },
  logoContainer: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  logo: {
    width: 180,
    height: 90,
    objectFit: 'contain',
  },
  cropSection: {
    marginTop: 16,
  },
  cropRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
    padding: '8px 0',
    borderBottomWidth: 1,
    borderBottomColor: colors.borderColor,
  },
  workerStats: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  statBox: {
    width: '48%',
    padding: 16,
    backgroundColor: colors.tertiary,
    borderRadius: 12,
    marginBottom: 8,
  },
  statBoxLabel: {
    fontSize: 10,
    color: colors.textSecondary,
    marginBottom: 4,
  },
  statBoxValue: {
    fontSize: 12,
    fontWeight: 'bold',
    color: colors.textPrimary,
  },
  inspectionItem: {
    marginBottom: 16,
    padding: 16,
    backgroundColor: colors.tertiary,
    borderRadius: 12,
  },
  inspectionTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    color: colors.textPrimary,
    marginBottom: 8,
  },
  inspectionDate: {
    fontSize: 10,
    color: colors.textSecondary,
    marginBottom: 4,
  },
  inspectionStatus: {
    fontSize: 10,
    color: colors.success,
    marginBottom: 8,
  },
  inspectionDescription: {
    fontSize: 10,
    color: colors.textPrimary,
    marginTop: 8,
  },
  chip: {
    backgroundColor: colors.secondary,
    color: colors.info,
    padding: '3px 8px',
    borderRadius: 8,
    fontSize: 8,
    marginLeft: 8,
  },
  mainCropChip: {
    backgroundColor: colors.secondary,
    color: colors.info,
    padding: '3px 8px',
    borderRadius: 8,
    fontSize: 8,
  },
  textField: {
    backgroundColor: colors.tertiary,
    borderRadius: 12,
    padding: '10px 14px',
    marginBottom: 8,
    flexDirection: 'row',
    alignItems: 'center',
  },
  fieldLabel: {
    width: '35%',
    fontSize: 10,
    color: colors.textSecondary,
  },
  fieldValue: {
    flex: 1,
    fontSize: 10,
    color: colors.textPrimary,
    fontWeight: 'bold',
  },
  pieChartContainer: {
    height: 120,
    marginTop: 16,
    marginBottom: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  legendContainer: {
    marginTop: 12,
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
    width: '45%',
  },
  legendColor: {
    width: 8,
    height: 8,
    marginRight: 6,
    borderRadius: 4,
  },
  legendText: {
    fontSize: 8,
    color: colors.textPrimary,
    flex: 1,
  },
  ownerSection: {
    marginTop: 8,
    padding: 8,
    backgroundColor: colors.tertiaryLight,
    borderRadius: 8,
  },
  ownerTitle: {
    fontSize: 9,
    fontWeight: 'bold',
    color: colors.textPrimary,
    marginBottom: 4,
  },
  ownerInfo: {
    fontSize: 8,
    color: colors.textSecondary,
  },
  ownerChip: {
    backgroundColor: colors.secondary,
    color: colors.info,
    padding: '2px 6px',
    borderRadius: 8,
    fontSize: 8,
    marginLeft: 6,
    alignSelf: 'flex-start',
  },
  levelTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: colors.textPrimary,
    marginBottom: 12,
    marginTop: 8,
  },
  challengesContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  challengeBox: {
    height: 32,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: colors.borderColor,
    minWidth: 32,
  },
  challengeBoxText: {
    fontSize: 9,
    fontWeight: 'bold',
  },
  approvedItem: {
    marginBottom: 16,
    padding: 16,
    backgroundColor: colors.tertiaryLight,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.borderColor,
  },
  approvedItemWrapper: {
    marginBottom: 16,
  },
  approvedItemHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
    gap: 12,
  },
  approvedItemName: {
    fontSize: 11,
    fontWeight: 'bold',
    flex: 1,
    color: colors.textPrimary,
  },
  questionText: {
    fontSize: 10,
    fontWeight: 'bold',
    color: colors.textPrimary,
    marginBottom: 4,
  },
  forEachContextLabel: {
    fontSize: 11,
    fontWeight: 'bold',
    color: colors.info,
    marginBottom: 8,
    marginTop: 12,
  },
  subQuestionText: {
    fontSize: 9,
    fontWeight: '600',
    color: colors.textSecondary,
    marginBottom: 4,
    textTransform: 'uppercase',
  },
  answerText: {
    fontSize: 10,
    color: colors.textPrimary,
    marginBottom: 8,
    fontWeight: 'bold',
  },
  answerImage: {
    width: '100%',
    minHeight: 140,
    maxHeight: 250,
    borderRadius: 12,
    objectFit: 'contain',
    marginBottom: 8,
  },
  answerImageLarge: {
    width: '100%',
    minHeight: 180,
    maxHeight: 300,
    borderRadius: 12,
    objectFit: 'contain',
    marginBottom: 8,
  },
  link: {
    fontSize: 8,
    color: colors.info,
    textDecoration: 'underline',
    marginBottom: 4,
  },
  videoPlaceholder: {
    width: '100%',
    minHeight: 140,
    maxHeight: 250,
    borderRadius: 12,
    backgroundColor: colors.divider,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 8,
  },
  videoPlaceholderLarge: {
    width: '100%',
    minHeight: 180,
    maxHeight: 300,
    borderRadius: 12,
    backgroundColor: colors.divider,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 8,
  },
  videoPlaceholderText: {
    fontSize: 10,
    color: colors.textSecondary,
  },
  attachmentError: {
    fontSize: 10,
    color: colors.error,
    marginBottom: 4,
  },
  imageGridContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    gap: 8,
  },
  imageGridItem: {
    width: '48%',
    marginBottom: 12,
  },
  imageGridItemFull: {
    width: '100%',
    marginBottom: 12,
  },
  trainingTitleCard: {
    marginBottom: 0,
    padding: 16,
    backgroundColor: colors.backgroundPaper,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: colors.borderColor,
  },
  trainingTitleCardFirst: {
    marginBottom: 0,
    padding: 20,
    backgroundColor: colors.tertiaryLight,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
  },
  trainingTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: colors.primary,
    margin: 0,
  },
  trainingLevelSection: {
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: colors.borderColor,
  },
  trainingLevelTitle: {
    fontSize: 13,
    fontWeight: 'bold',
    color: colors.primary,
    marginBottom: 12,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  trainingModuleCard: {
    padding: 12,
    backgroundColor: '#FAFBFF',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E8ECFF',
  },
  trainingSection: {
    marginBottom: 0,
    padding: 16,
    backgroundColor: colors.backgroundPaper,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: colors.borderColor,
  },
  trainingSectionNoRadius: {
    marginBottom: 0,
    marginTop: 0,
    padding: 12,
    backgroundColor: colors.backgroundPaper,
  },
  trainingSectionLast: {
    marginBottom: 0,
    marginTop: 0,
    padding: 12,
    backgroundColor: colors.backgroundPaper,
    borderBottomLeftRadius: 16,
    borderBottomRightRadius: 16,
  },
  trainingContainer: {
    borderRadius: 16,
    overflow: 'hidden',
    backgroundColor: colors.backgroundPaper,
    borderWidth: 1,
    borderColor: colors.borderColor,
    marginTop: 20,
  },
  trainingSectionContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  trainingLeftSection: {
    width: '50%',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  trainingIconContainer: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  trainingIconImage: {
    width: 36,
    height: 36,
    objectFit: 'cover',
  },
  trainingModuleTitle: {
    fontSize: 10,
    fontWeight: '600',
    color: colors.primary,
    flex: 1,
  },
  trainingRightSection: {
    width: '50%',
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    justifyContent: 'space-between',
  },
  trainingTopicsList: {
    flexDirection: 'column',
    gap: 6,
    flex: 1,
  },
  trainingTopicItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  trainingCheckboxCompleted: {
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: colors.trainingCheckbox,
    alignItems: 'center',
    justifyContent: 'center',
  },
  trainingCheckboxIncomplete: {
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: colors.trainingCheckboxBg,
  },
  trainingTopicName: {
    fontSize: 9,
    color: colors.primary,
    flex: 1,
  },
  trainingProgressBadge: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: colors.trainingProgressBg,
    alignItems: 'center',
    justifyContent: 'center',
  },
  trainingProgressText: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#FFFFFF',
  },
  trainingDivider: {
    marginVertical: 0,
    height: 1,
    backgroundColor: colors.borderColor,
  },
  mapImage: {
    width: '100%',
    height: 160,
    borderRadius: 12,
    marginBottom: 8,
    objectFit: 'cover',
  },
  coordinatesText: {
    fontSize: 9,
    color: colors.textSecondary,
    textAlign: 'center',
    fontFamily: 'Courier',
  },
});

interface FarmDetailsPDFProps {
  orgDetails: any;
  cityName?: string;
  corregimientoName?: string;
  veredaName?: string;
  regionName?: string;
  sortedLevels?: [string, any[]][];
  sortedApprovedLevels?: [string, any[]][];
  farmItemsMap?: { [pviId: string]: any };
  inspectedUserPhones?: string[];
  headerLogoDataUrl?: string | null;
  videoThumbnails?: Record<string, string | null>;
  imageDataUrls?: Record<string, string | null>;
  attachmentContentTypes?: Record<string, string | null>;
  pdfHeaderTitle?: string; // Dynamic title for PDF header
  pdfHeaderSubtitle?: string; // Dynamic subtitle for PDF header
  pdfSectionChallenges?: boolean; // Dynamic visibility for challenges section
  pdfSectionTrainings?: boolean; // Dynamic visibility for trainings section
  pdfSectionAttachments?: boolean; // Dynamic visibility for attachments section
  mapImageDataUrl?: string | null; // Map image data URL for PDF
  mapCoordinates?: string | null; // Formatted coordinates for PDF
  trainingProgressData?: Array<{
    module: TrainingModuleDto;
    topics: Array<{
      topic: TrainingTopicDto;
      isCompleted: boolean;
      contents: TrainingContentDto[];
    }>;
    progressPercentage: number;
  }>;
  formsMap?: Record<string, any>;
  answersMap?: Record<string, any[]>;
  showSensitiveData?: boolean;
}

const fetchAsDataURL = async (url: string, options?: RequestInit): Promise<string | null> => {
  if (!url) return null;
  try {
    const response = await fetch(url, options);
    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`);
    }
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = (error) => {
        console.error("FileReader error:", error);
        reject(error);
      };
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error(`Error fetching or converting image from ${url}:`, error);
    return null; // Return null on error
  }
};

const RenderUrl: React.FC<{ 
  url: string; 
  attachmentContentTypes: Record<string, string | null>, 
  videoThumbnails: Record<string, string | null>, 
  imageDataUrls: Record<string, string | null>,
  imageStyle?: any
}> = ({ url, attachmentContentTypes, videoThumbnails, imageDataUrls, imageStyle }) => {
  const contentType = attachmentContentTypes[url];
  const videoThumbnail = videoThumbnails[url];
  const imageDataUrl = imageDataUrls[url];
  const imageStyleToUse = imageStyle || styles.answerImage;

  const renderWithLink = (content: React.ReactNode) => {
    let linkText = 'Abrir adjunto';
    if (contentType?.startsWith('image/')) {
      linkText = 'Descargar imagen';
    } else if (contentType?.startsWith('video/')) {
      linkText = 'Ver video';
    } else if (contentType?.includes('pdf')) {
      linkText = 'Abrir PDF';
    }

    return (
      <View wrap={true} {...({minPresence: 50} as any)}>
        {content}
        <Link src={url || "#"} style={styles.link} {...({} as any)}>{linkText}</Link>
      </View>
    );
  };

  if (contentType === null) {
    return renderWithLink(
      <Text style={styles.attachmentError}>Adjunto (no se pudo previsualizar)</Text>
    );
  }

  if (contentType?.startsWith('video/')) {
    // Use same size logic as images: large style for single, regular for grid
    const videoPlaceholderStyle = imageStyle === styles.answerImageLarge 
      ? styles.videoPlaceholderLarge 
      : styles.videoPlaceholder;
    
    const videoContent = videoThumbnail
      ? <Image src={videoThumbnail} style={imageStyleToUse} />
      : <View style={videoPlaceholderStyle}><Text style={styles.videoPlaceholderText}>Video adjunto</Text></View>;

    return renderWithLink(
      videoContent
    );
  }

  if (contentType?.includes('pdf')) {
    return renderWithLink(<Text style={styles.answerText}>PDF adjunto</Text>);
  }

  // It is important to try to render as an image even if the content type is not explicitly image/*
  // For example, some servers return application/octet-stream for images.
  // Use data URL if available (more reliable for PDF rendering), otherwise use the original URL
  if (contentType?.startsWith('image/') || contentType === undefined) {
    const imageSrc = imageDataUrl || url;
    return renderWithLink(<Image src={imageSrc} style={imageStyleToUse} />);
  }

  // Fallback for other content types
  return renderWithLink(<Text style={styles.answerText}>Adjunto</Text>);
};

const PDFFormAnswersViewer: React.FC<{ formTemplateJson?: string; formAnswersJson?: string; form?: any; answers?: any[]; attachmentContentTypes: Record<string, string | null>, videoThumbnails: Record<string, string | null>, imageDataUrls: Record<string, string | null> }> = ({
  formTemplateJson,
  formAnswersJson,
  form,
  answers,
  attachmentContentTypes,
  videoThumbnails,
  imageDataUrls,
}) => {
  // Use new forms API if form exists and we have actual answers (non-empty array)
  // If form exists but answers are empty, fall back to legacy formAnswersJson if available
  const hasForm = !!form;
  const hasNewApiAnswers = Array.isArray(answers) && answers.length > 0;
  const hasLegacyAnswers = !!formAnswersJson;
  const useNewApi = hasForm && (hasNewApiAnswers || !hasLegacyAnswers);

  if (!formAnswersJson && !useNewApi) return null;

  const isLikelyUrl = (text: any): text is string => {
    if (typeof text !== 'string') return false;
    return text.startsWith('http://') || text.startsWith('https://');
  };

  const renderSingleAnswer = (ans: any, imageStyle?: any) => {
    if (isLikelyUrl(ans)) {
      return <RenderUrl url={ans} attachmentContentTypes={attachmentContentTypes} videoThumbnails={videoThumbnails} imageDataUrls={imageDataUrls} imageStyle={imageStyle} />;
    }
    return <Text style={styles.answerText}>{String(ans)}</Text>;
  };

  const renderAnswerArray = (answers: any[]) => {
    // Filter for media URLs (images and videos) to display in grid
    const mediaUrls = answers.filter(ans => {
      if (!isLikelyUrl(ans)) return false;
      const contentType = attachmentContentTypes[ans];
      return contentType?.startsWith('image/') || contentType?.startsWith('video/') || contentType === undefined;
    });

    const otherAnswers = answers.filter(ans => !mediaUrls.includes(ans));

    // For multiselect labels (non-URLs), display as comma-separated text
    if (mediaUrls.length === 0 && otherAnswers.length > 0 && typeof otherAnswers[0] === 'string') {
      return <Text style={styles.answerText}>{otherAnswers.join(', ')}</Text>;
    }

    // Smart grid layout: 1 media → full width (larger), 2+ media → 2 columns
    const useFullWidth = mediaUrls.length === 1;
    const mediaStyle = useFullWidth ? styles.answerImageLarge : styles.answerImage;
    const gridItemStyle = useFullWidth ? styles.imageGridItemFull : styles.imageGridItem;

    return (
      <View wrap={true} {...({minPresence: 40} as any)}>
        {/* Media Grid (Images and Videos) */}
        {mediaUrls.length > 0 && (
          <View style={styles.imageGridContainer}>
            {mediaUrls.map((url, index) => (
              <View key={index} style={gridItemStyle} wrap={false}>
                {renderSingleAnswer(url, mediaStyle)}
              </View>
            ))}
          </View>
        )}

        {/* Other Answers */}
        {otherAnswers.length > 0 && (
          <View>
            {otherAnswers.map((ans, index) => (
              <View key={index} style={{ marginBottom: 4 }}>
                {renderSingleAnswer(ans)}
              </View>
            ))}
          </View>
        )}
      </View>
    );
  };

  let formDefinition: any = null;
  if (useNewApi && form) {
    // Use the form object directly from new API
    formDefinition = form;
  } else if (formTemplateJson) {
    try { formDefinition = JSON.parse(formTemplateJson); } catch (e) { /* ignore */ }
  }

  // Helper function to parse "invalid JSON" array format like "[Seco, Baba]" or "[Seco]"
  const parseInvalidJSONArray = (str: string): string[] | string => {
    const inner = str.slice(1, -1).trim();
    if (!inner) return [];
    const items = inner.split(',').map(s => s.trim()).filter(s => s !== '');
    return items.length === 1 ? items[0] : items;
  };

  let processedAnswers: any;
  if (useNewApi && answers) {
    // Convert AnswerDto[] to FormAnswers format
    processedAnswers = answers.map((a: any) => ({
      answerId: a.id,
      questionId: a.questionId,
      value: (() => {
        // Safely parse JSON values that are stored as strings
        if (typeof a.value === 'string') {
          if ((a.value.startsWith('[') && a.value.endsWith(']')) ||
              (a.value.startsWith('{') && a.value.endsWith('}'))) {
            try {
              return JSON.parse(a.value);
            } catch {
              // Not valid JSON, try parsing the "invalid JSON" format like "[Seco, Baba]"
              if (a.value.startsWith('[') && a.value.endsWith(']')) {
                return parseInvalidJSONArray(a.value);
              }
              return a.value;
            }
          }
        }
        return a.value;
      })(),
      forEachContextValue: a.forEachContextValue,
      parentContextId: a.parentContextId,
    }));
  } else {
    try {
      processedAnswers = JSON.parse(formAnswersJson || '{}');
    } catch (e) {
      return renderSingleAnswer(formAnswersJson || '');
    }
  }

  if (formDefinition?.questions) {
    const formAnswers = Array.isArray(processedAnswers)
      ? processedAnswers.reduce((acc, answer) => ({ ...acc, [answer.questionId]: answer.value }), {})
      : processedAnswers;

    if (Object.keys(formAnswers).length === 0) return null;

    // Identify source questions that trigger FOR_EACH logic to hide them from main list
    const sourceQuestionsToHide = new Set<string>();
    formDefinition.questions.forEach((q: any) => {
      // condition_type is lowercase after formDtoToForm conversion ('for_each')
      if (q.display_logic?.condition_type === 'for_each' && q.display_logic.source_multiselect_id) {
        sourceQuestionsToHide.add(q.display_logic.source_multiselect_id);
      }
    });

    // Filter out FOR_EACH questions and their source multiselect questions
    const regularQuestions = formDefinition.questions.filter((q: any) => {
      // Skip FOR_EACH questions - they should only appear within their context groups
      if (q.display_logic?.condition_type === 'for_each') return false;
      // Skip questions that are source multiselects for FOR_EACH questions (check both uuid and id)
      if (sourceQuestionsToHide.has(q.uuid) || sourceQuestionsToHide.has(q.id)) return false;
      return true;
    });

    // Group FOR_EACH answers for rendering
    const forEachAnswersList = Array.isArray(processedAnswers)
      ? processedAnswers.filter((a: any) => a.forEachContextValue)
      : [];

    const groupedForEachAnswers: Record<string, any> = {};
    forEachAnswersList.forEach((answer: any) => {
      const contextKey = answer.parentContextId || answer.forEachContextValue;
      if (!contextKey) return;

      if (!groupedForEachAnswers[contextKey]) {
        groupedForEachAnswers[contextKey] = {
          contextValue: answer.forEachContextValue,
          parentContextId: answer.parentContextId,
          answers: {}
        };
      }
      groupedForEachAnswers[contextKey].answers[answer.questionId] = answer.value;
    });

    // Helper to get display value (resolve option labels, format dates)
    const getDisplayValue = (value: any, question?: any) => {
      if (!value) return value;
      // 1. Resolve Options (Select/Multiselect)
      if (question?.options && question.options.length > 0) {
        if (Array.isArray(value)) {
          return value.map((v: any) => {
            const option = question.options?.find((opt: any) => opt.value === v);
            return option?.label || v;
          });
        }
        const option = question.options?.find((opt: any) => opt.value === value);
        if (option?.label) return option.label;
      }
      // 2. Format Dates (ISO strings) - hide time if 00:00:00
      if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)) {
        try {
          const date = new Date(value);
          // Check if time is 00:00:00 - if so, show only date without time
          const isMidnight = date.getHours() === 0 && date.getMinutes() === 0 && date.getSeconds() === 0;
          const formatString = isMidnight ? 'd MMMM yyyy' : 'd MMMM yyyy, HH:mm';
          return format(date, formatString, { locale: es });
        } catch (e) {
          return value;
        }
      }
      return value;
    };

    // Helper to find source question ID with fallback
    const findSourceQuestionId = (forEachQuestion: any, parentContextId?: string) => {
      let sourceQuestionId = forEachQuestion?.display_logic?.source_multiselect_id;
      if (!sourceQuestionId && parentContextId) {
        // First check if it's a UUID format (contains hyphens in standard UUID pattern)
        const uuidMatch = parentContextId.match(/^([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})_/i);
        if (uuidMatch) {
          // It's a UUID format - find question by uuid
          const parentUuid = uuidMatch[1];
          const parentQuestion = formDefinition?.questions.find((q: any) => q.uuid === parentUuid);
          sourceQuestionId = parentQuestion?.uuid || parentQuestion?.id;
        } else {
          // StringId format - extract everything before the last underscore
          const lastUnderscoreIndex = parentContextId.lastIndexOf('_');
          if (lastUnderscoreIndex > 0) {
            const parentStringId = parentContextId.substring(0, lastUnderscoreIndex);
            const parentQuestion = formDefinition?.questions.find((q: any) => q.id === parentStringId);
            sourceQuestionId = parentQuestion?.uuid || parentQuestion?.id;
          }
        }
      }
      return sourceQuestionId;
    };

    const findQuestionById = (questionId: string) => {
      return formDefinition?.questions.find((q: any) => q.id === questionId || q.uuid === questionId);
    };

    const getContextLabel = (contextValue: string, sourceQuestionId: string | null) => {
      if (!sourceQuestionId) return contextValue;
      const sourceQuestion = findQuestionById(sourceQuestionId);

      if (!sourceQuestion?.options || sourceQuestion.options.length === 0) {
        return contextValue;
      }

      // Try exact match first
      let sourceOption = sourceQuestion.options?.find((opt: any) => opt.value === contextValue);

      // If not found, try with type conversion (contextValue might be "1" but option value is 1 or vice versa)
      if (!sourceOption) {
        sourceOption = sourceQuestion.options?.find((opt: any) => String(opt.value) === String(contextValue));
      }

      return sourceOption?.label || contextValue;
    };

    return (
      <View wrap={true} {...({minPresence: 15} as any)}>
        {/* Regular questions */}
        {regularQuestions.map((q: any) => {
          // Try both uuid and id to find the answer (uuid is backend UUID, id is stringId)
          const answer = formAnswers[q.uuid] ?? formAnswers[q.id];
          if (answer === undefined || answer === null || answer === '') return null;

          const displayValue = getDisplayValue(answer, q);

          return (
            <View key={q.id} style={{ marginBottom: 8 }} wrap={true}>
              <Text style={styles.questionText}>{q.text}</Text>
              {Array.isArray(displayValue) ? renderAnswerArray(displayValue) : renderSingleAnswer(displayValue)}
            </View>
          );
        })}

        {/* FOR_EACH question groups */}
        {Object.entries(groupedForEachAnswers).map(([contextKey, contextData]: [string, any]) => {
          const { contextValue, parentContextId, answers: groupAnswers } = contextData;
          const firstAnswerQuestionId = Object.keys(groupAnswers)[0];
          const forEachQuestionDef = findQuestionById(firstAnswerQuestionId);
          const sourceQuestionId = findSourceQuestionId(forEachQuestionDef, parentContextId);
          const contextLabel = getContextLabel(contextValue, sourceQuestionId);

          return (
            <View key={contextKey} style={{ marginBottom: 12, marginTop: 12 }} wrap={true} {...({minPresence: 15} as any)}>
              <Text style={styles.forEachContextLabel}>{contextLabel}</Text>

              {Object.entries(groupAnswers).map(([questionId, value]: [string, any]) => {
                const question = findQuestionById(questionId);
                if (!question || value === undefined || value === null || value === '') return null;

                let questionText = question.text;
                if (question.display_logic?.context_label_template) {
                  questionText = question.display_logic.context_label_template.replace(/\{label\}/g, contextLabel);
                }

                const displayValue = getDisplayValue(value, question);

                return (
                  <View key={questionId} style={{ marginBottom: 6, marginLeft: 6 }} wrap={true}>
                    <Text style={styles.subQuestionText}>{questionText}</Text>
                    {Array.isArray(displayValue) ? renderAnswerArray(displayValue) : renderSingleAnswer(displayValue)}
                  </View>
                );
              })}
            </View>
          );
        })}
      </View>
    );
  }

  if (Array.isArray(processedAnswers)) return renderAnswerArray(processedAnswers);
  if (typeof processedAnswers === 'object' && processedAnswers !== null) return null;
  return renderSingleAnswer(processedAnswers);
};

const PieChart: React.FC<{ data: any[] }> = ({ data }) => {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  let currentAngle = 0;

  return (
    <Svg width={100} height={100} viewBox="0 0 100 100">
      {data.map((item, index) => {
        const percentage = (item.value / total) * 100;
        const angle = (percentage / 100) * 360;
        const x1 = 50 + 40 * Math.cos((currentAngle * Math.PI) / 180);
        const y1 = 50 + 40 * Math.sin((currentAngle * Math.PI) / 180);
        const x2 = 50 + 40 * Math.cos(((currentAngle + angle) * Math.PI) / 180);
        const y2 = 50 + 40 * Math.sin(((currentAngle + angle) * Math.PI) / 180);
        
        const path = `M 50 50 L ${x1} ${y1} A 40 40 0 ${angle > 180 ? 1 : 0} 1 ${x2} ${y2} Z`;
        currentAngle += angle;

        return (
          <Path
            key={index}
            d={path}
            fill={item.color}
          />
        );
      })}
      <Circle cx={50} cy={50} r={25} fill="white"/>
    </Svg>
  );
};

const TrainingProgressPDF: React.FC<{ trainingProgressData: Array<{
  module: TrainingModuleDto;
  topics: Array<{
    topic: TrainingTopicDto;
    isCompleted: boolean;
    contents: TrainingContentDto[];
  }>;
  progressPercentage: number;
}> }> = ({ trainingProgressData }) => {
  if (!trainingProgressData || trainingProgressData.length === 0) {
    return null;
  }

  // School/Education icon as SVG for placeholder
  const SchoolIconSVG = () => (
    <Svg width="24" height="24" viewBox="0 0 24 24">
      <Path
        d="M12 3L1 9L5 11.18V17.18L12 21L19 17.18V11.18L21 10.09V17H23V9L12 3ZM18.82 9L12 12.72L5.18 9L12 5.28L18.82 9ZM17 15.99L12 18.72L7 15.99V12.27L12 15L17 12.27V15.99Z"
        fill="#FFFFFF"
      />
    </Svg>
  );

  // Group sections by training (assuming all modules in a training share the same training context)
  // For simplicity, we'll treat each module as part of a training and group them
  // In a real scenario, you might have a trainingId to group by
  
  return (
    <View style={styles.trainingContainer} {...({minPresence: 15} as any)}>
      {/* Main title */}
      <View style={styles.trainingTitleCardFirst}>
        <Text style={styles.trainingTitle}>Desarrollo de Capacidades</Text>
      </View>
      <View style={styles.trainingDivider} />
      
      {/* Training sections */}
      {trainingProgressData.map((section, index) => {
        const isLast = index === trainingProgressData.length - 1;
        return (
          <React.Fragment key={section.module.id}>
            {/* Training name as subtitle */}
            <View style={styles.trainingLevelSection}>
              <Text style={styles.trainingLevelTitle}>{section.module.name}</Text>
              
              {/* Module card */}
              <View style={styles.trainingModuleCard} wrap={false}>
                <View style={styles.trainingSectionContent}>
                  {/* Left: Module (Icon and Title) */}
                  <View style={styles.trainingLeftSection}>
                    <View style={styles.trainingIconContainer}>
                      {section.module.logo ? (
                        <Image src={section.module.logo} style={styles.trainingIconImage} />
                      ) : (
                        <SchoolIconSVG />
                      )}
                    </View>
                    <Text style={styles.trainingModuleTitle}>{section.module.name}</Text>
                  </View>

                  {/* Right: Topics list and Progress badge */}
                  <View style={styles.trainingRightSection}>
                    <View style={styles.trainingTopicsList}>
                      {section.topics.map(({ topic, isCompleted }) => (
                        <View key={topic.id} style={styles.trainingTopicItem}>
                          <View style={isCompleted ? styles.trainingCheckboxCompleted : styles.trainingCheckboxIncomplete}>
                            {isCompleted && (
                              <Text style={{ color: '#FFFFFF', fontSize: 7, fontWeight: 'bold' }}>★</Text>
                            )}
                          </View>
                          <Text style={styles.trainingTopicName}>{topic.name}</Text>
                        </View>
                      ))}
                    </View>
                    {/* Progress percentage badge */}
                    <View style={styles.trainingProgressBadge}>
                      <Text style={styles.trainingProgressText}>
                        {section.progressPercentage}%
                      </Text>
                    </View>
                  </View>
                </View>
              </View>
            </View>
            {index < trainingProgressData.length - 1 && <View style={styles.trainingDivider} />}
          </React.Fragment>
        );
      })}
    </View>
  );
};

export const FarmDetailsPDF: React.FC<FarmDetailsPDFProps> = ({
  orgDetails,
  cityName,
  corregimientoName,
  veredaName,
  regionName,
  sortedLevels,
  sortedApprovedLevels,
  farmItemsMap,
  inspectedUserPhones,
  headerLogoDataUrl,
  videoThumbnails = {},
  imageDataUrls = {},
  attachmentContentTypes = {},
  pdfHeaderTitle,
  pdfHeaderSubtitle,
  pdfSectionChallenges,
  pdfSectionTrainings,
  pdfSectionAttachments,
  mapImageDataUrl = null,
  mapCoordinates = null,
  trainingProgressData = [],
  formsMap = {},
  answersMap = {},
  showSensitiveData = false,
}) => {
  const fetchAsDataURL = async (url: string): Promise<string | null> => {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`);
      }
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = (error) => {
          console.error("FileReader error:", error);
          reject(error);
        };
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error(`Error fetching or converting image from ${url}:`, error);
      return null; // Return null on error
    }
  };

  const farmLocation = orgDetails?.farmLocations[0];
  const socioeconomics = orgDetails?.farmSocioeconomics[0];
  const totalArea = orgDetails?.farmCrops?.reduce((sum: number, crop: any) => sum + crop.area, 0) || 0;

  const cropData = orgDetails?.farmCrops?.map((crop: any, index: number) => ({
    id: crop.id,
    value: crop.area,
    name: crop.name,
    color: crop.isMain ? '#2196f3' : `hsl(${(index * 30) % 360}, 70%, 60%)`,
  })) || [];

  const renderText = (text: string) => {
    try {
      return text.toString();
    } catch (error) {
      console.error('Error converting text:', error);
      return '';
    }
  };

  // Helper to check if endDate is previous year (local time)
  const isEndDatePreviousYear = (endDate: string | null | undefined): boolean => {
    if (!endDate) return false;
    const end = new Date(endDate);
    const now = new Date();
    return end.getFullYear() === now.getFullYear() - 1;
  };

  // Get dynamic PDF header title or fallback to default
  const getPDFHeaderTitle = (): string => {
    // If pdfHeaderTitle is provided and not empty, use it
    if (pdfHeaderTitle && pdfHeaderTitle.trim() !== '') {
      return pdfHeaderTitle;
    }
    
    // Fallback to hardcoded title
    return '';
  };

  // Get dynamic PDF header subtitle or fallback to default
  const getPDFHeaderSubtitle = (): string => {
    // If pdfHeaderSubtitle is provided and not empty, use it
    if (pdfHeaderSubtitle && pdfHeaderSubtitle.trim() !== '') {
      return pdfHeaderSubtitle;
    }
    
    // Fallback to hardcoded subtitle
    return 'HOJA DE VIDA';
  };

  // Get dynamic challenges section visibility or fallback to default
  const getPDFChallengesVisibility = (): boolean => {
    // If pdfSectionChallenges is provided, use it
    if (pdfSectionChallenges !== undefined) {
      return pdfSectionChallenges;
    }
    
    // Fallback: show challenges section by default
    return true;
  };

  // Get dynamic attachments section visibility or fallback to default
  const getPDFAttachmentsVisibility = (): boolean => {
    // If pdfSectionAttachments is provided, use it
    if (pdfSectionAttachments !== undefined) {
      return pdfSectionAttachments;
    }
    
    // Fallback: show attachments section by default
    return true;
  };

  // Get dynamic trainings section visibility or fallback to default
  const getPDFTrainingsVisibility = (): boolean => {
    // If pdfSectionTrainings is explicitly provided (including false), use it
    // This ensures consistent behavior whether the prop is explicitly set or uses fallback
    if (pdfSectionTrainings !== undefined) {
      return pdfSectionTrainings;
    }
    
    // Fallback: hide trainings section by default (secure by default)
    // This matches the behavior in FarmDetailsModal.getDynamicPDFTrainingsVisibility()
    return false;
  };

  return (
    <Document>
      <Page size="LETTER" style={styles.page}>
        {/* Fixed Header - Repeats on every page automatically */}
        <View fixed style={styles.topHeader}>
          {headerLogoDataUrl && <Image src={headerLogoDataUrl} style={styles.topHeaderLogo} />}
          <View style={styles.verticalDivider} />
          <View style={styles.topHeaderTextContainer}>
            <Text style={styles.topHeaderTitle}>{getPDFHeaderTitle()}</Text>
            <Text style={styles.topHeaderSubtitle}>{getPDFHeaderSubtitle()}</Text>
          </View>
        </View>
        
        {/* Header Section */}
        {/* <View style={styles.section}>
          <View style={styles.header}>
            <View>
              <Text style={styles.title}>{renderText(orgDetails.name)}</Text>
              <Text style={styles.subtitle}>
                {renderText(orgDetails.idType.name)}: {renderText(orgDetails.idNumber)}
              </Text>
            </View>
            {orgDetails.logo && (
              <Image src={orgDetails.logo} style={styles.logo} />
            )}
          </View>
        </View> */}



        {/* Two Column Layout */}
        <View style={styles.contentContainer}>
          {/* Left Column */}
          <View style={styles.leftColumn}>
                    {/* Datos Generales Section */}
        <View style={styles.section} wrap={false}>
          <Text style={styles.sectionTitle}>Datos generales</Text>
          {showSensitiveData && (
            <View style={styles.textField}>
              <Text style={styles.fieldLabel}>Nombre y Apellido:</Text>
              <Text style={styles.fieldValue}>
                {orgDetails.organizationsUsers?.[0]?.user?.name}
              </Text>
            </View>
          )}
          {showSensitiveData && (
            <View style={styles.textField}>
              <Text style={styles.fieldLabel}>Nombre de la Finca:</Text>
              <Text style={styles.fieldValue}>{renderText(orgDetails.name)}</Text>
            </View>
          )}
          <View style={styles.textField}>
            <Text style={styles.fieldLabel}>{renderText(orgDetails.idType?.name || 'Cédula')}:</Text>
            <Text style={styles.fieldValue}>{renderText(orgDetails.idNumber)}</Text>
          </View>
          {showSensitiveData && (
            <View style={styles.textField}>
              <Text style={styles.fieldLabel}>Contacto:</Text>
              <Text style={styles.fieldValue}>
                {inspectedUserPhones && inspectedUserPhones.length > 0
                  ? inspectedUserPhones.map(formatPhoneNumber).join(', ')
                  : (orgDetails.organizationsUsers?.[0]?.user?.phone ? formatPhoneNumber(orgDetails.organizationsUsers?.[0]?.user?.phone) : 'N/A')}
              </Text>
            </View>
          )}
          <View style={styles.textField}>
            <Text style={styles.fieldLabel}>Departamento:</Text>
            <Text style={styles.fieldValue}>{regionName || 'N/A'}</Text>
          </View>
          <View style={styles.textField}>
            <Text style={styles.fieldLabel}>Municipio:</Text>
            <Text style={styles.fieldValue}>{cityName || 'N/A'}</Text>
          </View>
          {veredaName && (
            <View style={styles.textField}>
              <Text style={styles.fieldLabel}>Vereda:</Text>
              <Text style={styles.fieldValue}>{veredaName}</Text>
            </View>
          )}
        </View>
            {/* Location Information */}
            {/* 
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Ubicación</Text>
              {cityName && (
                <View style={styles.textField}>
                  <Text style={styles.fieldLabel}>Ciudad:</Text>
                  <Text style={styles.fieldValue}>{renderText(cityName)}</Text>
                </View>
              )}
              {corregimientoName && (
                <View style={styles.textField}>
                  <Text style={styles.fieldLabel}>Corregimiento:</Text>
                  <Text style={styles.fieldValue}>{renderText(corregimientoName)}</Text>
                </View>
              )}
              {veredaName && (
                <View style={styles.textField}>
                  <Text style={styles.fieldLabel}>Vereda:</Text>
                  <Text style={styles.fieldValue}>{renderText(veredaName)}</Text>
                </View>
              )}
              {farmLocation && (
                <>
                  <View style={styles.textField}>
                    <Text style={styles.fieldLabel}>Área:</Text>
                    <Text style={styles.fieldValue}>{renderText(farmLocation.area)} Ha</Text>
                  </View>
                  <View style={styles.textField}>
                    <Text style={styles.fieldLabel}>Altura:</Text>
                    <Text style={styles.fieldValue}>{renderText(farmLocation.elevation)} m</Text>
                  </View>
                  <View style={styles.textField}>
                    <Text style={styles.fieldLabel}>Coordenadas:</Text>
                    <Text style={styles.fieldValue}>
                      {renderText(farmLocation.latitude)}°N, {renderText(farmLocation.longitude)}°W
                    </Text>
                  </View>
                </>
              )}
            </View> */}

            {/* Workers Information */}
            {/* socioeconomics && (
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>Información de Trabajadores</Text>
                <View style={styles.workerStats}>
                  <View style={styles.statBox}>
                    <Text style={styles.statBoxLabel}>Hombres Tiempo Completo</Text>
                    <Text style={styles.statBoxValue}>{renderText(socioeconomics.malesFullTimeWorkers)}</Text>
                  </View>
                  <View style={styles.statBox}>
                    <Text style={styles.statBoxLabel}>Hombres Temporales</Text>
                    <Text style={styles.statBoxValue}>{renderText(socioeconomics.malesTemporalWorkers)}</Text>
                  </View>
                  <View style={styles.statBox}>
                    <Text style={styles.statBoxLabel}>Mujeres Tiempo Completo</Text>
                    <Text style={styles.statBoxValue}>{renderText(socioeconomics.femaleFullTimeWorkers)}</Text>
                  </View>
                  <View style={styles.statBox}>
                    <Text style={styles.statBoxLabel}>Mujeres Temporales</Text>
                    <Text style={styles.statBoxValue}>{renderText(socioeconomics.femaleTemporalWorkers)}</Text>
                  </View>
                </View>
              </View>
            )} */}  

            {/* Inspections moved under Workers Information */}
            {/*  orgDetails.inspectionsReceived && orgDetails.inspectionsReceived.length > 0 && (
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>Inspecciones</Text>
                {orgDetails.inspectionsReceived.map((inspection: any) => (
                  <View key={inspection.id} style={styles.inspectionItem}>
                    <Text style={styles.inspectionTitle}>{renderText(inspection.name)}</Text>
                    <Text style={styles.inspectionDate}>
                      {format(new Date(inspection.startDate), 'MMM dd, yyyy')} - 
                      {format(new Date(inspection.endDate), 'MMM dd, yyyy')}
                    </Text>
                    {inspection.closeDate && (
                      <Text style={styles.inspectionStatus}>
                        Completada el: {format(new Date(inspection.closeDate), 'MMM dd, yyyy')}
                      </Text>
                    )}
                    {inspection.description && (
                      <Text style={styles.inspectionDescription}>{renderText(inspection.description)}</Text>
                    )}
                  </View>
                ))}
              </View>
            )} */}
          </View>

          {/* Right Column */}
          <View style={styles.rightColumn}>
            {/* Organization Logo - only show if NO map */}
            {orgDetails.logo && !mapImageDataUrl && (
              <View style={styles.section}>
                <View style={styles.logoContainer}>
                  <Image src={orgDetails.logo} style={styles.logo} />
                </View>
              </View>
            )}

            {/* Location Map */}
            {mapImageDataUrl && (
              <View style={[styles.section, { marginTop: 0 }]} wrap={false}>
                <Text style={styles.sectionTitle}>Ubicación</Text>
                <Image src={mapImageDataUrl} style={styles.mapImage} />
                {mapCoordinates && (
                  <Text style={styles.coordinatesText}>{mapCoordinates}</Text>
                )}
              </View>
            )}

            {/* Crops Information with Pie Chart */}
            {orgDetails.farmCrops && orgDetails.farmCrops.length > 0 && (
              <View style={[styles.section, { marginTop: 0 }]} wrap={false}>
                <Text style={styles.sectionTitle}>Cultivos</Text>
                <Text style={styles.subtitle}>Área Total: {totalArea.toFixed(2)} Ha</Text>
                
                <View style={styles.pieChartContainer}>
                  <PieChart data={cropData} />
                </View>

                <View style={styles.legendContainer}>
                  {cropData.map((crop: any) => (
                    <View key={crop.id} style={styles.legendItem}>
                      <View style={[styles.legendColor, { backgroundColor: crop.color }]} />
                      <Text style={styles.legendText}>
                        {crop.name} ({((crop.value / totalArea) * 100).toFixed(1)}%)
                      </Text>
                    </View>
                  ))}
                </View>

                {orgDetails.farmCrops.map((crop: any) => (
                  <View key={crop.id} style={styles.cropRow}>
                    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                      <Text style={styles.value}>{renderText(crop.name)}</Text>
                      {crop.isMain && <Text style={styles.mainCropChip}>Principal</Text>}
                    </View>
                    <Text style={styles.value}>
                      {crop.area.toFixed(2)} Ha
                    </Text>
                  </View>
                ))}
              </View>
            )}
          </View>
        </View>

        {/* Challenges Summary (Full Width) */}
        {getPDFChallengesVisibility() && sortedLevels && sortedLevels.length > 0 && (
          <View style={[styles.trainingContainer, styles.fullWidthSection]} {...({minPresence: 15} as any)}>
            <View style={styles.trainingTitleCardFirst}>
              <Text style={styles.trainingTitle}>Datos de la Finca</Text>
            </View>
            <View style={styles.trainingDivider} />
            {sortedLevels.map(([levelName, pvis], index) => {
              // Calculate maximum width needed for all boxes in this level
              // Approximate: fontSize 9, bold, padding 8px each side, min 28px
              const textWidths = pvis.map(item => {
                // Handle both plain pvi objects and { ...farmItem, pvi } structure
                const pvi = item.pvi || item;
                const text = pvi?.cpNumber?.split('.').pop() || '';
                // Approximate width: ~6px per character for fontSize 9 bold
                return Math.max(28, (text.length * 6) + 16); // 16px for padding (8px each side)
              });
              const maxWidth = Math.max(...textWidths, 28);

              return (
                <React.Fragment key={levelName}>
                  {index > 0 && <View style={styles.trainingDivider} />}
                  <View style={styles.trainingSectionNoRadius} wrap={true} {...({minPresence: 50} as any)}>
                    <Text style={styles.levelTitle}>{levelName.toLowerCase()}</Text>
                    <View style={styles.challengesContainer}>
                      {pvis.map(item => {
                        // Handle both plain pvi objects and { ...farmItem, pvi } structure
                        const pvi = item.pvi || item;
                        const farmItem = item.pvi ? item : (farmItemsMap ? farmItemsMap[pvi.id] : null);

                        // Default: grey for pending/unknown (design system: textDisabled)
                        let boxBgColor = colors.textDisabled;
                        let boxBorderColor = colors.textDisabled;
                        let boxTextColor = '#fff';

                        if (farmItem) {
                          if (farmItem.isApproved) {
                            boxBgColor = colors.success; // Green
                            boxBorderColor = colors.success;
                            boxTextColor = '#fff';
                          } else if (farmItem.isActive) {
                            boxBgColor = colors.warning; // Orange
                            boxBorderColor = colors.warning;
                            boxTextColor = '#fff';
                          }
                        }

                        // Special case: approved but with previous year end date (blue - OVERRIDES green)
                        const isApprovedButEmptyValue = farmItem && farmItem.isApproved && isEndDatePreviousYear(farmItem.endDate);
                        if (isApprovedButEmptyValue) {
                          boxBgColor = colors.info; // Blue for "A" status
                          boxBorderColor = colors.info;
                          boxTextColor = colors.infoDark;
                        }

                        return (
                          <View key={pvi?.id || item.id} style={[
                            styles.challengeBox,
                            {
                              backgroundColor: boxBgColor,
                              borderColor: boxBorderColor,
                              width: maxWidth,
                              minWidth: 32
                            }
                          ]}>
                            <Text style={[styles.challengeBoxText, { color: boxTextColor }]}>
                              {pvi?.cpNumber?.split('.').pop() || ''}
                            </Text>
                          </View>
                        );
                      })}
                    </View>
                  </View>
                </React.Fragment>
              );
            })}
          </View>
        )}


        {/* Training Progress Section (Full Width) */}
        {getPDFTrainingsVisibility() && trainingProgressData && trainingProgressData.length > 0 && (
          <View style={styles.fullWidthSection}>
            <TrainingProgressPDF trainingProgressData={trainingProgressData} />
          </View>
        )}

        {/* Approved Item Attachments (Full Width) */}
        {getPDFAttachmentsVisibility() && sortedApprovedLevels && sortedApprovedLevels.length > 0 && (
          <View style={[styles.trainingContainer, styles.fullWidthSection]} {...({minPresence: 15} as any)}>
            <View style={styles.trainingTitleCardFirst}>
              <Text style={styles.trainingTitle}>Adjuntos</Text>
            </View>
            <View style={styles.trainingDivider} />
            
            {sortedApprovedLevels.map(([levelName, items], index) => {
              const isLast = index === sortedApprovedLevels.length - 1;
              return (
              <React.Fragment key={levelName}>
                {index > 0 && <View style={styles.trainingDivider} />}
                <View style={isLast ? styles.trainingSectionLast : styles.trainingSectionNoRadius}>
                  <Text style={styles.levelTitle}>{levelName.toLowerCase()}</Text>
                  {items.map((item: any) => (
                    <View key={item.id} style={styles.approvedItem} wrap={true} {...({minPresence: 15} as any)}>
                      <View style={styles.approvedItemHeader}>
                        {(() => {
                          // Default: grey for pending/unknown (design system: textDisabled)
                          let boxBgColor = colors.textDisabled;
                          let boxBorderColor = colors.textDisabled;
                          let boxTextColor = '#fff';

                          if (item.isApproved) {
                            boxBgColor = colors.success; // Green
                            boxBorderColor = colors.success;
                            boxTextColor = '#fff';
                          } else if (item.isActive) {
                            boxBgColor = colors.warning; // Orange
                            boxBorderColor = colors.warning;
                            boxTextColor = '#fff';
                          }

                          // Special case: approved but with previous year end date (blue - OVERRIDES green)
                          const isApprovedButEmptyValue = item.isApproved && isEndDatePreviousYear(item.endDate);
                          if (isApprovedButEmptyValue) {
                            boxBgColor = colors.info;
                            boxBorderColor = colors.info;
                            boxTextColor = colors.infoDark;
                          }

                          return (
                            <View style={[
                                styles.challengeBox,
                                {
                                    backgroundColor: boxBgColor,
                                    borderColor: boxBorderColor,
                                    marginRight: 12,
                                    width: 'auto',
                                    minWidth: 32,
                                    paddingHorizontal: 10
                                }
                            ]}>
                              <Text style={[styles.challengeBoxText, { color: boxTextColor }]}>
                                {item.pvi?.cpNumber?.split('.').pop() || ''}
                              </Text>
                            </View>
                          );
                        })()}
                        <Text style={styles.approvedItemName}>{item.pvi?.name || ''}</Text>
                      </View>
                      <PDFFormAnswersViewer
                        form={item.pvi?.formId ? formsMap[item.pvi.formId] : undefined}
                        answers={item.pvi?.formId ? (answersMap[item.id] || []) : undefined}
                        formTemplateJson={item.pvi?.measurementTemplate || ''}
                        formAnswersJson={item.value}
                        attachmentContentTypes={attachmentContentTypes}
                        videoThumbnails={videoThumbnails}
                        imageDataUrls={imageDataUrls}
                      />
                    </View>
                  ))}
                </View>
              </React.Fragment>
              );
            })}
          </View>
        )}

        {/* Fixed Page Footer with page numbers */}
        <View fixed style={{ position: 'absolute', bottom: 15, left: 0, right: 0, textAlign: 'center', fontSize: 9, color: colors.textSecondary }}>
          <Text render={({ pageNumber, totalPages }) => `Página ${pageNumber} de ${totalPages}`} />
        </View>
      </Page>
    </Document>
  );
}; 